import { useState } from 'react';

import { toUserMessage } from '@platform/api-client';

import { useImportPumsemDivision } from '../../api/use-pumsem-manage.query';
import type {
  PumsemImportPayload,
  PumsemImportResult,
} from '../../model/pumsemManage.type';
import { BTN_DANGER, BTN_GHOST, LABEL } from './pumsemFormStyles';

interface PumsemImportPanelProps {
  edition: string;
  /**
   * 숨긴 판에서만 임포트가 된다. 서버도 같은 기준으로 막는다.
   * `undefined` 는 **아직 모른다**(관리 목록이 안 왔다) — "공개 중" 과 다르다.
   */
  isHidden: boolean | undefined;
  /** 이 판에 실린 부문 slug. 파일의 부문이 여기 없으면 미리 잡는다. */
  attached: string[];
}

/**
 * 서버가 이 경로에만 물려 둔 본문 상한(`core-service/main.ts` 의
 * `BODY_LIMIT.BULK_IMPORT`). 여기서 미리 재는 이유는, 넘겨 보내면 본문을 다
 * 올린 **뒤에** 413 이 오기 때문이다 — 2.5MB 를 올리고 나서 거절당하는 것보다
 * 고르는 순간 말해주는 쪽이 낫다. 서버 값이 바뀌면 여기도 같이 바꾼다.
 */
const IMPORT_SIZE = { bytesPerKb: 1024, kbPerMb: 1024, maxMb: 16 } as const;
const MB = IMPORT_SIZE.bytesPerKb * IMPORT_SIZE.kbPerMb;
const MAX_BYTES = IMPORT_SIZE.maxMb * MB;

/** 파일을 읽어 최소한의 모양만 본다. 나머지는 서버가 `ImportEditionDto` 로 본다. */
function parsePayload(text: string): PumsemImportPayload {
  const data: unknown = JSON.parse(text);
  if (
    typeof data !== 'object' ||
    data === null ||
    typeof (data as PumsemImportPayload).division !== 'string' ||
    !Array.isArray((data as PumsemImportPayload).items)
  ) {
    throw new Error('division 과 items 가 있는 임포트 JSON 이 아닙니다.');
  }
  return data as PumsemImportPayload;
}

/**
 * 일괄 임포트 — `tools/pumsem-extract` 가 뽑은 JSON 을 넣는다.
 *
 * ── 왜 브라우저가 넣는가 ────────────────────────────────────
 * 추출 스크립트가 직접 POST 하게 두면 관리자 토큰이 파일에 남고 만료 처리도
 * 붙어야 한다. 브라우저는 이미 세션을 들고 있다. 무엇보다 임포트는 **부문을
 * 통째로 갈아끼우는** 동작이라, `python push.py` 한 줄로 1,200건이 교체되면
 * 잘못 넣었을 때 되돌릴 방법이 "다시 뽑아서 다시 넣기" 뿐이다.
 *
 * 그래서 여기는 **확인 화면**이다: 무엇이 몇 건 바뀌는지 먼저 세어 보여주고,
 * 사람이 누른 뒤에 보낸다.
 */
export function PumsemImportPanel({
  edition,
  isHidden,
  attached,
}: PumsemImportPanelProps) {
  const [payload, setPayload] = useState<PumsemImportPayload | null>(null);
  const [fileName, setFileName] = useState('');
  const [fileBytes, setFileBytes] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PumsemImportResult | null>(null);

  const importDivision = useImportPumsemDivision(edition);

  const read = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResult(null);
    if (file.size > MAX_BYTES) {
      setPayload(null);
      setFileName('');
      setError(
        `파일이 ${(file.size / MB).toFixed(1)}MB 로 서버 한도(${IMPORT_SIZE.maxMb}MB)를 넘습니다. ` +
          '부문을 나눠 추출하거나(`--division`), 한 번에 넣는 항목 수를 줄이세요.',
      );
      return;
    }
    try {
      setPayload(parsePayload(await file.text()));
      setFileName(file.name);
      setFileBytes(file.size);
    } catch (cause) {
      setPayload(null);
      setFileName('');
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const notAttached = payload && !attached.includes(payload.division);

  return (
    <section className="mb-8 rounded-xl border border-neutral-200 p-4 dark:border-white/12">
      <h2 className={LABEL}>일괄 임포트</h2>

      {isHidden === undefined ? (
        <p className="text-[13px] text-neutral-500">개정판 정보를 불러오는 중…</p>
      ) : !isHidden ? (
        <p className="text-[13px] text-neutral-500">
          공개 중인 판에는 임포트할 수 없습니다. <b>숨기기</b> 를 먼저 누르세요 —
          임포트는 부문을 통째로 갈아끼우는 동작이라, 공개된 판에 돌리면 남의
          견적서가 인용한 근거가 조용히 다른 값이 됩니다.
        </p>
      ) : (
        <>
          <label
            className={`${BTN_GHOST} cursor-pointer focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-1`}
            htmlFor="pumsem-import"
          >
            JSON 파일 고르기
          </label>
          <input
            id="pumsem-import"
            type="file"
            accept="application/json,.json"
            className="sr-only"
            onChange={(event) => {
              void read(event.target.files?.[0]);
              // 같은 파일을 다시 고를 수 있어야 한다.
              event.target.value = '';
            }}
          />

          {payload && (
            <div className="mt-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3">
              <p className="text-[13.5px] text-amber-900 dark:text-amber-200">
                <b>{fileName}</b> ({(fileBytes / MB).toFixed(1)}MB) — 부문{' '}
                <b>{payload.division}</b> · 장·절 {payload.groups?.length ?? 0}개
                · 항목 {payload.items.length}건
              </p>
              <p className="mt-1.5 text-[12.5px] text-amber-900 dark:text-amber-200">
                이 판의 <b>{payload.division}</b> 부문 내용이 <b>전부 교체</b>
                됩니다. 항목 자체와 다른 판의 내용, 딥링크는 남습니다.
              </p>
              {notAttached && (
                <p className="mt-1.5 text-[12.5px] text-amber-900 dark:text-amber-200">
                  이 판에 아직 안 실린 부문입니다 — 임포트하면서 함께 실립니다.
                </p>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  className={BTN_DANGER}
                  disabled={importDivision.isPending}
                  onClick={() =>
                    importDivision.mutate(payload, {
                      onSuccess: (data) => {
                        setResult(data);
                        setPayload(null);
                        setFileName('');
                      },
                      onError: (cause) => setError(toUserMessage(cause)),
                    })
                  }
                >
                  {importDivision.isPending ? '넣는 중…' : '임포트'}
                </button>
                <button
                  type="button"
                  className={BTN_GHOST}
                  disabled={importDivision.isPending}
                  onClick={() => {
                    setPayload(null);
                    setFileName('');
                  }}
                >
                  취소
                </button>
              </div>

              {/* 수백 항목을 한 트랜잭션에서 갈아끼운다. 아무 안내 없이 30초가
                  지나면 사용자는 멈춘 줄 알고 새로고침하거나 다시 누른다. */}
              {importDivision.isPending && (
                <p
                  className="mt-2 text-[12.5px] text-amber-900 dark:text-amber-200"
                  role="status"
                >
                  항목 {payload.items.length}건을 넣는 중입니다 — 최대 1분까지
                  걸립니다. 이 화면을 닫지 마세요. 중간에 끊겨도 같은 파일을 다시
                  넣으면 되므로 데이터가 반쯤 들어가는 일은 없습니다.
                </p>
              )}
            </div>
          )}

          {result && (
            <p
              className="mt-3 text-[13px] text-emerald-700 dark:text-emerald-300"
              role="status"
            >
              {result.division} — 항목 {result.total}건 (신설 {result.created} ·
              개정 {result.amended} · 삭제 {result.removed}) 을 넣었습니다.
              <br />
              추출 리포트의 검수 목록을 열어 플래그 걸린 항목부터 확인하세요.
            </p>
          )}

          <p className="mt-2 text-[12px] text-neutral-500">
            <code>tools/pumsem-extract</code> 가 뽑은{' '}
            <code>out/&lt;판&gt;/&lt;부문&gt;.json</code> 을 넣습니다. 같은 파일을
            몇 번 넣어도 결과가 같습니다 — 부문 단위 전량 교체라서 그렇습니다.
          </p>
        </>
      )}

      {error && (
        <p className="mt-2 text-[13px] text-danger" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
